<template>
	<aside id="sidebar">
		<div class="env-banner" :class="`env-${ envKey }`">{{ envLabel }}</div>
		<header>
			<div class="header-actions">
				<layout-floated-button
					title="Documentation"
					@click="router.push('/documentation')">
					<cfl-icon icon="documentation"></cfl-icon>
				</layout-floated-button>
				<layout-floated-button
					title="Configuration de la box"
					@click="router.push('/box/settings')">
					<cfl-icon icon="settings"></cfl-icon>
				</layout-floated-button>
			</div>
			<RouterLink to="">
				<brand-logo width="300" class="logo"/>
			</RouterLink>
			<admin-activity-bell></admin-activity-bell>
		</header>

		<div class="main">
			<nav class="nav">
				<!-- Accueil -->
				<sidebar-accordion
					title="Accueil"
					icon="menu-home"
					pinned
					:active="isAccueilActive"
					icon-bg-class="bg-vert-sylvestre dark:bg-vert-de-hooker">
					<RouterLink to="/" class="nav-sub-item" :class="{ active: isThisItemActive('dashboard') }">
						<cfl-icon icon="menu-home"></cfl-icon>
						<span>Dashboard</span>
					</RouterLink>
					<RouterLink to="/users" class="nav-sub-item" :class="{ active: isThisItemActive('users') }">
						<cfl-icon icon="user-image-profile"></cfl-icon>
						<span>Clients</span>
						<template v-if="usersCount > 0">
							<span class="badge">{{ usersCount }}</span>
						</template>
					</RouterLink>
					<RouterLink to="/user-subscriptions" class="nav-sub-item" :class="{ active: isThisItemActive('user-subscriptions') }">
						<cfl-icon icon="user-subscriptions"></cfl-icon>
						<span>Abonnés</span>
						<template v-if="userSubscriptionsCount > 0">
							<span class="badge">{{ userSubscriptionsCount }}</span>
						</template>
					</RouterLink>
					<RouterLink to="/planning" class="nav-sub-item" :class="{ active: isThisItemActive('planning', 'planning-resume', 'planning-attendees', 'planning-medias') }">
						<cfl-icon icon="menu-planning"></cfl-icon>
						<span>Planning</span>
						<template v-if="pendingAttendeesCount > 0">
							<span class="badge">{{ pendingAttendeesCount }}</span>
						</template>
					</RouterLink>
					<RouterLink to="/workouts" class="nav-sub-item" :class="{ active: isThisItemActive('workouts', 'workout', 'workout-blocks', 'workout-actions', 'workout-templates') }">
						<cfl-icon icon="menu-workout"></cfl-icon>
						<span>Séances</span>
					</RouterLink>
					<RouterLink to="/events" class="nav-sub-item" :class="{ active: isThisItemActive('events') }">
						<cfl-icon icon="event"></cfl-icon>
						<span>Évènements</span>
					</RouterLink>
					<RouterLink to="/box/surveys" class="nav-sub-item" :class="{ active: isThisItemActive('surveys') }">
						<cfl-icon icon="survey"></cfl-icon>
						<span>Sondages</span>
					</RouterLink>
					<RouterLink to="/box/lost-items" class="nav-sub-item" :class="{ active: isThisItemActive('lost-items') }">
						<cfl-icon icon="lost-items"></cfl-icon>
						<span>Objets trouvés</span>
						<template v-if="Number(claimedLostItemsCount) > 0">
							<span class="badge">{{ Number(claimedLostItemsCount) }}</span>
						</template>
					</RouterLink>
					<div class="nav-sub-divider"></div>
					<RouterLink to="/sales" class="nav-sub-item" :class="{ active: isThisItemActive('/sales', '/sales/shop', '/sales/shop/add', '/sales/subscriptions', '/sales/drop-in', '/sales/events') }">
						<cfl-icon icon="revenue-coins"></cfl-icon>
						<span>Ventes</span>
					</RouterLink>
					<RouterLink to="/stats" class="nav-sub-item" :class="{ active: isThisItemActive('stats') }">
						<cfl-icon icon="workout-stats"></cfl-icon>
						<span>Statistiques</span>
					</RouterLink>
					<div class="nav-sub-divider"></div>
					<RouterLink to="/movements" class="nav-sub-item"
								:class="{ active: isThisItemActive('movements', 'movements-muscles', 'movement', 'movement-actions', 'movement-relations') }">
						<cfl-icon icon="movements-guide"></cfl-icon>
						<span>Mouvements</span>
					</RouterLink>
				</sidebar-accordion>

				<!-- Communication -->
				<sidebar-accordion
					title="Communication"
					icon="communication"
					:active="isCommunicationActive"
					pinned
					icon-bg-class="bg-bleu-ceruleen dark:bg-bleu-ceruleen-alt">
					<RouterLink to="/communication/broadcasts" class="nav-sub-item" :class="{ active: isThisItemActive('broadcasts') }">
						<cfl-icon icon="broadcast"></cfl-icon>
						<span>Au quotidien</span>
					</RouterLink>
					<RouterLink to="/box/news" class="nav-sub-item" :class="{ active: isThisItemActive('news') }">
						<cfl-icon icon="news"></cfl-icon>
						<span>Actualités</span>
					</RouterLink>
					<RouterLink to="/communication/pushes" class="nav-sub-item" :class="{ active: isThisItemActive('pushes') }">
						<cfl-icon icon="push-notification"></cfl-icon>
						<span>Push Notifications</span>
					</RouterLink>
					<RouterLink to="/communication/emails" class="nav-sub-item" :class="{ active: isThisItemActive('emails') }">
						<cfl-icon icon="email"></cfl-icon>
						<span>E-mails</span>
					</RouterLink>
					<RouterLink to="/box/contact-messages" class="nav-sub-item" :class="{ active: isThisItemActive('contact-messages') }">
						<cfl-icon icon="contact-message"></cfl-icon>
						<span>Messages reçus</span>
						<template v-if="unreadMessagesCount > 0">
							<span class="badge">{{ unreadMessagesCount }}</span>
						</template>
					</RouterLink>
				</sidebar-accordion>

				<!-- Boutique -->
				<sidebar-accordion
					title="Boutique"
					icon="shop"
					:active="isBoutiqueActive"
					pinned
					icon-bg-class="bg-violet-prune dark:bg-violet-prune-alt">
					<RouterLink to="/subscriptions" class="nav-sub-item" :class="{ active: isThisItemActive('subscriptions') }">
						<cfl-icon icon="subscriptions"></cfl-icon>
						<span>Abonnements</span>
					</RouterLink>
					<RouterLink to="/products" class="nav-sub-item" :class="{ active: isThisItemActive('products', 'suppliers') }">
						<cfl-icon icon="shop-textile"></cfl-icon>
						<span>Produits</span>
					</RouterLink>
					<RouterLink to="/stock" class="nav-sub-item"
								:class="{ active: isThisItemActive('stock', 'stock-movements', 'stock-expirations') }">
						<cfl-icon icon="shop-stock"></cfl-icon>
						<span>Stock</span>
					</RouterLink>
					<RouterLink to="/coupons" class="nav-sub-item" :class="{ active: isThisItemActive('/coupons') }">
						<cfl-icon icon="menu-coupon"></cfl-icon>
						<span>Coupons</span>
					</RouterLink>
				</sidebar-accordion>

				<!-- Référentiels -->
				<sidebar-accordion
					title="Référentiels"
					icon="tags"
					:active="isReferentielsActive"
					pinned
					icon-bg-class="bg-bleu-de-prusse dark:bg-bleu-de-prusse-alt">
					<RouterLink to="/product-categories" class="nav-sub-item" :class="{ active: isThisItemActive('product-categories') }">
						<cfl-icon icon="shop-category"></cfl-icon>
						<span>Catégories de produits</span>
					</RouterLink>
					<RouterLink to="/product-models" class="nav-sub-item" :class="{ active: isThisItemActive('product-models') }">
						<cfl-icon icon="shop-textile"></cfl-icon>
						<span>Modèles de produits</span>
					</RouterLink>
					<RouterLink to="/workout-tags" class="nav-sub-item" :class="{ active: isThisItemActive('workout-tags') }">
						<cfl-icon icon="tag"></cfl-icon>
						<span>Mots-clés de séance</span>
					</RouterLink>
					<RouterLink to="/event-tags" class="nav-sub-item" :class="{ active: isThisItemActive('event-tags') }">
						<cfl-icon icon="tag"></cfl-icon>
						<span>Mots-clés d'événement</span>
					</RouterLink>
					<RouterLink to="/workout-levels" class="nav-sub-item" :class="{ active: isThisItemActive('workout-levels') }">
						<cfl-icon icon="workout-level"></cfl-icon>
						<span>Niveaux de difficulté</span>
					</RouterLink>
					<RouterLink to="/workout-block-types" class="nav-sub-item" :class="{ active: isThisItemActive('workout-block-types') }">
						<cfl-icon icon="workout-block-type"></cfl-icon>
						<span>Types de blocs</span>
					</RouterLink>
					<RouterLink to="/workout-types" class="nav-sub-item" :class="{ active: isThisItemActive('workout-types') }">
						<cfl-icon icon="workout-types"></cfl-icon>
						<span>Types de séances</span>
					</RouterLink>
					<RouterLink to="/event-types" class="nav-sub-item" :class="{ active: isThisItemActive('event-types') }">
						<cfl-icon icon="event"></cfl-icon>
						<span>Types d'événement</span>
					</RouterLink>
					<RouterLink to="/places" class="nav-sub-item" :class="{ active: isThisItemActive('places') && ! route.path.startsWith('/places/routes') }">
						<cfl-icon icon="menu-places"></cfl-icon>
						<span>Lieux</span>
					</RouterLink>
					<RouterLink to="/places/routes" class="nav-sub-item" :class="{ active: route.path.startsWith('/places/routes') }">
						<cfl-icon icon="route"></cfl-icon>
						<span>Parcours</span>
					</RouterLink>
					<RouterLink to="/box/equipment" class="nav-sub-item" :class="{ active: isThisItemActive('equipment') }">
						<cfl-icon icon="equipment"></cfl-icon>
						<span>Équipement de la box</span>
					</RouterLink>
					<RouterLink to="/movement-families" class="nav-sub-item" :class="{ active: isThisItemActive('movement-families') }">
						<cfl-icon icon="families"></cfl-icon>
						<span>Familles de mouvements</span>
					</RouterLink>
					<div class="nav-sub-divider"></div>
					<RouterLink to="/anatomy" class="nav-sub-item" :class="{ active: isThisItemActive('anatomy') }">
						<cfl-icon icon="body-part"></cfl-icon>
						<span>Anatomie</span>
					</RouterLink>
					<RouterLink to="/medias" class="nav-sub-item" :class="{ active: isThisItemActive('medias') }">
						<cfl-icon icon="media"></cfl-icon>
						<span>Médias</span>
					</RouterLink>
				</sidebar-accordion>

				<!-- Système -->
				<sidebar-accordion
					title="Système"
					icon="settings"
					:active="isSystemActive"
					pinned
					icon-bg-class="bg-marron-sepia dark:bg-marron-sepia-alt">
					<RouterLink to="/api-monitor" class="nav-sub-item" :class="{ active: isThisItemActive('api-monitor') }">
						<cfl-icon icon="terminal"></cfl-icon>
						<span>Monitoring API</span>
					</RouterLink>
				</sidebar-accordion>
			</nav>
		</div>

		<footer>
			<div class="footer-row">
				<div class="avatar-block">
					<user-image-profile :image-profile="userImageProfile" size="sm"></user-image-profile>
					<div class="avatar-info">
						<div class="avatar-name">{{ userFirstname }}</div>
						<div class="avatar-role">Admin</div>
					</div>
				</div>
				<div class="footer-actions">
					<layout-floated-button
						:title="accessibilityStore.isDarkTheme ? 'Thème clair' : 'Thème sombre'"
						@click="toggleTheme">
						<cfl-icon :icon="accessibilityStore.isDarkTheme ? 'theme-light' : 'theme-dark'"></cfl-icon>
					</layout-floated-button>
					<layout-floated-button
						title="Déconnexion"
						@click="logoutDialog">
						<cfl-icon icon="account-logout"></cfl-icon>
					</layout-floated-button>
				</div>
			</div>
		</footer>
	</aside>

	<cfl-dialog id="dialog-logout" ref="dialogLogout" size="sm" title="Déconnexion">
		Voulez-vous vous déconnecter de votre session ?
		<template v-slot:buttons>
			<cfl-button color="rouge-auburn" label="Se déconnecter" @click="clickOnLogout"></cfl-button>
		</template>
	</cfl-dialog>
</template>

<script lang="ts" setup>
	import CflDialog from "@shared-components-cfl/cfl-dialog.vue";
	import CflButton from "@shared-components-cfl/cfl-button.vue";
	import CflIcon from "@shared-components-cfl/cfl-icon.vue";
	import LayoutFloatedButton from "@shared-components/layout/layout-floated-button.vue";
	import UserImageProfile from "@shared-components/user/user-image-profile.vue";
	import SidebarAccordion from "@/components/layout/sidebar-accordion.vue";
	import BrandLogo from "@shared-components/brand/brand-logo.vue";
	import AdminActivityBell from "@/components/layout/admin-activity-bell.vue";
	import { useRoute, useRouter } from "vue-router";
	import useAuthStore from "@shared-stores/auth.store";
	import useAccessibilityStore from "@shared-stores/accessibility.store";
	import useLoginState from "@shared-composables/useLoginState";
	import DialogInterface from "@shared-interfaces/dialog.interface";

	import { computed, onMounted, onUnmounted, ref } from "vue";
	import useAdminStatsStore from "@/store/admin.stats.store";

	const router = useRouter();
	const route = useRoute();
	const accessibilityStore = useAccessibilityStore();
	const authStore = useAuthStore();
	const adminStatsStore = useAdminStatsStore();
	const { loggedUser } = useLoginState();

	const dialogLogout = ref<DialogInterface | null>(null);

	const usersCount = computed(() => adminStatsStore.usersCount);
	const userSubscriptionsCount = computed(() => adminStatsStore.userSubscriptionsCount);
	const unreadMessagesCount = computed(() => adminStatsStore.unreadMessagesCount);
	const claimedLostItemsCount = computed(() => adminStatsStore.claimedLostItemsCount);
	const pendingAttendeesCount = computed(() => adminStatsStore.pendingAttendeesCount);

	const userFirstname = computed(() => loggedUser.value?.firstname ?? "Admin");
	const userImageProfile = computed(() => loggedUser.value?.imageProfile);

	const logoutDialog = () =>
	{
		dialogLogout.value?.openDialog();
	};

	const isThisItemActive = (...urls: string[]): boolean =>
	{
		for (const url of urls)
		{
			if (router.currentRoute.value.fullPath == url)
			{
				return true;
			}

			if (router.currentRoute.value.name == url)
			{
				return true;
			}
		}

		return false;
	};

	// Une catégorie est active si la route courante commence par un de ses préfixes
	const isCategoryActive = (...prefixes: string[]): boolean =>
	{
		const path = router.currentRoute.value.fullPath;
		return prefixes.some(p =>
		{
			if (p === "/")
			{
				return path === "/";
			}
			return path === p || path.startsWith(p + "/") || path.startsWith(p + "?");
		});
	};

	const envKey = computed<string>(() =>
	{
		const env = (import.meta.env.VITE_ENV ?? "dev").toString().toLowerCase();
		if (env.startsWith("prod"))
		{
			return "production";
		}
		if (env.startsWith("stag"))
		{
			return "staging";
		}
		return "dev";
	});

	const envLabel = computed<string>(() =>
	{
		switch (envKey.value)
		{
			case "production":
				return "Production";
			case "staging":
				return "Staging";
			default:
				return "Développement";
		}
	});

	const isAccueilActive = computed(() => isCategoryActive("/", "/users", "/user-subscriptions", "/planning", "/workouts", "/workout-templates", "/events", "/box/surveys", "/box/lost-items", "/sales", "/stats", "/activities", "/movements"));
	const isCommunicationActive = computed(() => isCategoryActive("/communication/broadcasts", "/box/news", "/communication/pushes", "/communication/emails", "/box/contact-messages"));
	const isBoutiqueActive = computed(() => isCategoryActive("/subscriptions", "/products", "/suppliers", "/stock", "/coupons"));
	const isReferentielsActive = computed(() => isCategoryActive("/workout-tags", "/workout-levels", "/workout-block-types", "/workout-types", "/event-types", "/event-tags", "/product-categories", "/product-models", "/places", "/box/equipment", "/movement-families", "/anatomy", "/medias"));
	const isSystemActive = computed(() => isCategoryActive("/api-monitor"));

	const clickOnLogout = async () =>
	{
		authStore.logout();
		await router.push("/login");
	};


	const toggleTheme = () =>
	{
		if (accessibilityStore.isDarkTheme)
		{
			accessibilityStore.disableDarkTheme();
		}
		else
		{
			accessibilityStore.enableDarkTheme();
		}
	};

	/** Raccourci clavier Ctrl+T pour basculer le thème (parité avec le web). */
	const onKeyDown = (e: KeyboardEvent) =>
	{
		if (e.ctrlKey && e.key.toLowerCase() === "t")
		{
			e.preventDefault();
			toggleTheme();
		}
	};

	onMounted(() => window.addEventListener("keydown", onKeyDown));
	onUnmounted(() => window.removeEventListener("keydown", onKeyDown));
</script>

<style scoped>
	@reference "@main-css";

	#sidebar {
		@apply bg-vert-de-hooker dark:bg-vert-sylvestre;
		@apply text-white shrink-0 z-40;
		@apply hidden md:flex md:flex-col md:h-full md:w-[240px];
		@apply lg:w-[270px];

		> header {
			@apply relative w-full flex flex-col shrink-0 justify-center items-center;
			@apply border-b border-white/20;
			@apply bg-vert-sylvestre;

			.logo {
				@apply block m-4 p-0 cursor-pointer;
				@apply w-[100px];
				@apply lg:w-[120px];
				@apply drop-shadow-2xl;
			}

			&:deep(.activity-bell) {
				@apply absolute top-2 right-2;
			}

			.header-actions {
				@apply absolute top-2 left-2 flex flex-col gap-2;
			}
		}

		> div.main {
			@apply grow overflow-y-auto;
			@apply min-h-0;

			.nav {
				@apply flex flex-col;
			}
		}

		> footer {
			@apply w-full shrink-0 flex flex-col gap-2 p-2;
			@apply border-t border-white/20;
			@apply bg-vert-sylvestre;
		}
	}

	.env-banner {
		@apply text-center py-1.5 px-2 text-white text-xs font-semibold uppercase tracking-wider;
		@apply shrink-0;

		&.env-dev {
			@apply bg-bleu-ceruleen;
		}

		&.env-staging {
			@apply bg-orange-mandarine;
		}

		&.env-production {
			@apply bg-rouge-auburn;
		}
	}

	.nav-item {
		@apply relative flex items-center gap-3 px-3 py-2.5 rounded-md;
		@apply text-base text-white;
		@apply hover:bg-vert-sylvestre dark:hover:bg-vert-de-hooker;

		&.active {
			@apply bg-vert-anthracite;

			&::before {
				content: "";
				@apply absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-sm bg-white;
			}
		}

		.label {
			@apply min-w-0 flex-1 truncate;
		}

		.chevron {
			@apply opacity-50 w-4 h-4 shrink-0;
		}
	}

	/* Sous-items des accordions (dans le flow de la sidebar — fond vert sombre) */

	.nav-sub-item {
		@apply relative flex items-center gap-2 px-3 py-2 w-full;
		@apply text-sm text-white/85 no-underline cursor-pointer;
		@apply bg-transparent border-0 text-left;
		@apply hover:bg-vert-sylvestre hover:text-white;
		@apply dark:hover:bg-white/10 dark:hover:text-white;

		&:deep([data-icon]) {
			@apply w-4 h-4 shrink-0;
		}

		&:hover:deep([data-icon]) {
			@apply rotate-icon-sm;
		}

		.badge {
			@apply badge-pill ml-auto;
		}

		&.active {
			@apply text-white font-medium;
			@apply bg-black/20 dark:bg-white/10;

			/* Hover sur item déjà actif → bg plus marqué pour feedback visuel. */

			&:hover {
				@apply bg-black/40 dark:bg-white/20;
			}
		}

	}

	.nav-sub-divider {
		@apply h-px bg-white/15 mx-2;
	}

	.footer-row {
		@apply flex flex-row items-center gap-2;

		.avatar-block {
			@apply flex items-center gap-3 rounded-md grow min-w-0;
			@apply text-white;

			.avatar-circle {
				@apply w-10 h-10 rounded-full;
				@apply bg-vert-anthracite;
				@apply flex items-center justify-center;
				@apply text-sm font-semibold text-white shrink-0;
			}

			.avatar-info {
				@apply min-w-0;

				.avatar-name {
					@apply text-sm font-medium truncate;
				}

				.avatar-role {
					@apply text-xs opacity-70 truncate;
				}
			}
		}

		.footer-actions {
			@apply flex flex-row gap-1 shrink-0;
		}
	}

	.flyout-divider {
		@apply h-px bg-gray-2 dark:bg-dark-5 my-1 mx-2;
	}

</style>
